import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { HttpApiService } from "@/shared/services/httpApi.service";

import type { AssignedRole } from "../types/access.types";

/** Map of principal id → its currently assigned roles. */
export type RolesByPrincipal = Record<string, AssignedRole[]>;

/** Stable identity for "no principal failed", so consumers keyed on this Set don't re-run. */
const EMPTY_UNAVAILABLE: ReadonlySet<string> = new Set<string>();

/**
 * What a surface asks its principals endpoint for. Filters are optional because only the users binding has
 * them — `/api-keys` accepts nothing but `skip`/`limit` and ignores the rest.
 */
export interface PrincipalPageQuery {
  skip: number;
  limit: number;
  search?: string;
  status?: string;
  roleId?: string;
}

export interface PrincipalPageResult<TPrincipal> {
  data: TPrincipal[];
  total: number;
  /**
   * Roles the server already resolved for the whole page (`?include=roles`). When present the per-principal
   * fan-out is skipped entirely — that is what kills the N+1. When absent the hook falls back to one
   * `listRoles` call per row, which is still what the API keys binding does.
   */
  rolesById?: RolesByPrincipal;
}

/**
 * Endpoint bindings for one principal kind (users or API keys). Must be **referentially stable** — define it
 * at module scope. The hook keys its callbacks on this object, and their identity feeds
 * `AccessDataProvider`'s mount effect; an inline object would recreate `load` every render and re-trigger an
 * infinite reload.
 */
export interface PrincipalRolesConfig<TPrincipal extends { id: string }> {
  /** Fetches one page of principals plus the backend's full count for the query. */
  fetchPage: (query: PrincipalPageQuery) => Promise<PrincipalPageResult<TPrincipal>>;
  /** `GET` the principal's currently assigned roles. Unused when `fetchPage` returns `rolesById`. */
  listRoles: (principalId: string) => Promise<AssignedRole[]>;
  /** `POST` role ids; the response is the principal's full new role set. */
  assignRoles: (principalId: string, roleIds: string[]) => Promise<AssignedRole[]>;
  /** `DELETE` one role assignment. */
  removeRole: (principalId: string, roleId: string) => Promise<void>;
  /** Page size for the first load, before any `loadWith`. */
  initialLimit: number;
}

/**
 * Shared state machine for the principal tables (Usuarios, API Keys): loads one page of principals and, per
 * principal, its assigned roles in parallel — the `Promise.all` fan-out pattern from
 * `useWalletRecentActivity`. Exposes auto-save mutators used by the assign-roles drawer: each toggle persists
 * immediately (POST add / DELETE remove) and updates the local map so the table's effective count reflects
 * it. `total` is the backend's full count, so a section can disclose when more principals exist than the
 * capped page shows.
 *
 * `useAccessUsers` and `useAccessApiKeys` bind this to their endpoints and rename the fields; only the
 * bindings differ between them.
 */
export function usePrincipalRoles<TPrincipal extends { id: string }>(
  config: PrincipalRolesConfig<TPrincipal>,
) {
  const [items, setItems] = useState<TPrincipal[]>([]);
  const [total, setTotal] = useState(0);
  const [rolesById, setRolesById] = useState<RolesByPrincipal>({});
  // Ids whose per-principal roles fetch FAILED. Kept apart from `rolesById` so the table can show
  // "roles unavailable" (unknown) for them instead of an authoritative "no access".
  const [rolesUnavailable, setRolesUnavailable] = useState<ReadonlySet<string>>(EMPTY_UNAVAILABLE);
  const [isLoading, setIsLoading] = useState(true);
  const [hasNoAccess, setHasNoAccess] = useState(false);

  // Monotonic load counter: concurrent `load()` calls (initial mount + a section's reload) can resolve out of
  // order, and the slower response must never overwrite the fresher one. Only the latest invocation may write
  // state. Bumped on unmount so an in-flight load can't set state on an unmounted hook.
  const loadSeq = useRef(0);
  useEffect(
    () => () => {
      loadSeq.current += 1;
    },
    [],
  );

  // The active query lives in a ref, not in `load`'s closure. That is what keeps `load` referentially
  // stable while the filters change: `AccessDataProvider`'s mount effect and `RolesSection`'s reload both
  // depend on `load`'s identity, so rebuilding it per keystroke would re-fetch the whole catalog.
  const queryRef = useRef<PrincipalPageQuery>({ skip: 0, limit: config.initialLimit });

  /**
   * Principals mutated since the in-flight load started.
   *
   * A load that was issued BEFORE a toggle carries the pre-mutation roles for that principal, and letting it
   * land would silently revert the pill — the backend right, the UI wrong, which on a permissions screen is
   * the worst outcome. But only THAT principal is stale: `items`, `total` and everyone else's roles are still
   * the answer to the current query, so discarding the whole response would throw away a filter the user
   * asked for. We keep the response and re-apply the mutated entries over it.
   */
  const mutatedDuringLoad = useRef<Set<string>>(new Set());

  /**
   * Merge a freshly loaded roles map, letting any concurrent mutation win.
   *
   * `failed` is merged the same way: a principal we just mutated is by definition KNOWN, so a `listRoles`
   * that failed for it inside the same load must not flip its row back to "roles unavailable" on top of the
   * authoritative answer we already hold. Only the fan-out path can produce that (the `include=roles` path
   * has no per-principal failure), so in practice this guards API keys.
   */
  const commitRoles = useCallback((fresh: RolesByPrincipal, failed: ReadonlySet<string>) => {
    // Captured at call time, not read inside the updater: `runFetch` reassigns this ref, and closing over
    // the value keeps the updater pure instead of depending on when React flushes the queue.
    const mutated = mutatedDuringLoad.current;

    setRolesById((prev) => {
      if (mutated.size === 0) return fresh;
      const merged = { ...fresh };
      for (const id of mutated) {
        // `prev` already holds the post-mutation truth for this principal, whichever way it was changed.
        if (id in prev) merged[id] = prev[id];
      }
      return merged;
    });

    if (mutated.size === 0) {
      setRolesUnavailable(failed.size === 0 ? EMPTY_UNAVAILABLE : failed);
      return;
    }
    const stillUnknown = new Set(failed);
    for (const id of mutated) stillUnknown.delete(id);
    setRolesUnavailable(stillUnknown.size === 0 ? EMPTY_UNAVAILABLE : stillUnknown);
  }, []);

  const runFetch = useCallback(
    async (query: PrincipalPageQuery) => {
      const seq = ++loadSeq.current;
      mutatedDuringLoad.current = new Set();
      setIsLoading(true);
      setHasNoAccess(false);
      try {
        const response = await config.fetchPage(query);

        // Fast path: the server resolved the page's roles in the same response, so there is no
        // per-principal failure to record. An empty `rolesUnavailable` here is honest, not a loss of
        // information — either the whole request succeeded or it threw.
        if (response.rolesById) {
          if (seq !== loadSeq.current) return;
          setItems(response.data);
          setTotal(response.total);
          commitRoles(response.rolesById, EMPTY_UNAVAILABLE);
          return;
        }

        const failed = new Set<string>();
        const entries = await Promise.all(
          response.data.map(async (item) => {
            try {
              return [item.id, await config.listRoles(item.id)] as const;
            } catch {
              // A single principal's roles failing must not blank the whole table — but record it so the row
              // is rendered as "unavailable" (unknown), not silently as a confirmed "no roles" / "no access".
              failed.add(item.id);
              return [item.id, [] as AssignedRole[]] as const;
            }
          }),
        );
        if (seq !== loadSeq.current) return; // stale response — a newer load owns the state
        // Atomic write after the fan-out completes: the list and its roles map land together, so no render
        // ever sees fresh principals with a stale (or empty) assignment map.
        setItems(response.data);
        setTotal(response.total);
        commitRoles(Object.fromEntries(entries), failed);
      } catch (error) {
        if (seq !== loadSeq.current) return;
        if (HttpApiService.isAccessDeniedError(error)) {
          setHasNoAccess(true);
          setItems([]);
          setTotal(0);
          setRolesById({});
          setRolesUnavailable(EMPTY_UNAVAILABLE);
          return;
        }
        throw error;
      } finally {
        if (seq === loadSeq.current) setIsLoading(false);
      }
    },
    [config, commitRoles],
  );

  /** Reload the CURRENT query. Zero args and referentially stable — the identity consumers depend on. */
  const load = useCallback(() => runFetch(queryRef.current), [runFetch]);

  /** Change the query and load it. Stable too, so a filters effect can depend on it safely. */
  const loadWith = useCallback(
    (next: PrincipalPageQuery) => {
      queryRef.current = next;
      return runFetch(next);
    },
    [runFetch],
  );

  const assignRoles = useCallback(
    async (principalId: string, roleIds: string[]): Promise<AssignedRole[]> => {
      const updated = await config.assignRoles(principalId, roleIds);
      // Mark this principal so a load issued before the POST cannot land its pre-mutation roles on top
      // (see `mutatedDuringLoad`). The load itself is left alone: it still owns `items`/`total`.
      mutatedDuringLoad.current.add(principalId);
      setRolesById((prev) => ({ ...prev, [principalId]: updated }));
      // The assign response is the authoritative role set, so this id is no longer "unavailable".
      setRolesUnavailable((prev) => {
        if (!prev.has(principalId)) return prev;
        const next = new Set(prev);
        next.delete(principalId);
        return next;
      });
      return updated;
    },
    [config],
  );

  const removeRole = useCallback(
    async (principalId: string, roleId: string): Promise<void> => {
      await config.removeRole(principalId, roleId);
      // Same concurrent-load protection as `assignRoles`.
      mutatedDuringLoad.current.add(principalId);
      setRolesById((prev) => ({
        ...prev,
        [principalId]: (prev[principalId] ?? []).filter((role) => role.id !== roleId),
      }));
    },
    [config],
  );

  /** Patch one principal in place (e.g. an API key's `allowed_ips` after a lifecycle call). */
  const patchItem = useCallback((id: string, patch: (item: TPrincipal) => TPrincipal) => {
    setItems((prev) => prev.map((item) => (item.id === id ? patch(item) : item)));
  }, []);

  // Memoized so the renamed projections in useAccessUsers / useAccessApiKeys can depend on this single object.
  return useMemo(
    () => ({
      items,
      total,
      rolesById,
      rolesUnavailable,
      isLoading,
      hasNoAccess,
      load,
      loadWith,
      assignRoles,
      removeRole,
      patchItem,
    }),
    [
      items,
      total,
      rolesById,
      rolesUnavailable,
      isLoading,
      hasNoAccess,
      load,
      loadWith,
      assignRoles,
      removeRole,
      patchItem,
    ],
  );
}
